"use client";
import { Link } from "@/i18n/routing";
import { getInvestingSignals } from "@/lib/cryptoData/bitcoin";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useTheme } from "../theme/ThemeContext";
import LockIcon from "./icons/lockIcon";
import InvestingBar from "./InvestingBar";
import InvestingGauge from "./InvestingGauge";

export default function InvestingSignals({ crypto }: { crypto: string }) {
  const t = useTranslations();
  const theme = useTheme();
  const [trendHelpOpen, setTrendHelpOpen] = useState(false);
  const [valuationHelpOpen, setValuationHelpOpen] = useState(false);
  const [signals, setSignals] = useState<any>(null);
  const [showMask, setShowMask] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const handleTrendHelpOpen = () => {
    setTrendHelpOpen(true);
  };

  const handleTrendHelpClose = () => {
    setTrendHelpOpen(false);
  };

  const handleValuationHelpOpen = () => {
    setValuationHelpOpen(true);
  };

  const handleValuationHelpClose = () => {
    setValuationHelpOpen(false);
  };

  async function loadData() {
    const res = await getInvestingSignals(crypto);
    if (res.success) {
      setSignals(res.data);
    } else if (res.error?.code === "Server.notSubUser") {
      setShowMask(true);
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.paperMedium",
        borderRadius: 3,
        width: { xs: "100%", md: "auto" },
        mb: { xs: 1, md: 0 },
        minHeight: { xs: "20rem", md: "auto" },
        minWidth: { xs: "100%", md: "37%", lg: "33%" },
        display: "flex",
        flexDirection: "column",
      }}>
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 1.5}}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
            }}>
            {t("Metrics.signal.trendIndicator.title")}
          </Typography>

          <IconButton onClick={handleTrendHelpOpen} size="small" sx={{ padding: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: "1.125rem", color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.tertiary",
            fontSize: "var(--font-size-2xs)",
          }}>
          {/* 更新时间显示 */}
          {t("Metrics.proof.update", {
            date: new Date().toISOString().slice(0, 10),
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 2,
          pt: crypto === "btc" ? 3.5 : 8,
          position: "relative",
          flex: 1,
          minHeight: 0,
        }}>
        <Box sx={{ pointerEvents: showMask ? "none" : "auto", transition: "filter 0.3s" }}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <InvestingGauge value={signals?.trendIndicator || 0} />
          </Box>

          {crypto === "btc" && (
            <div>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, pt: 4, pb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}>
                  {t("Metrics.signal.marketValuation.title")}
                </Typography>
                <IconButton onClick={handleValuationHelpOpen} size="small" sx={{ padding: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: "1.125rem", color: "text.secondary" }} />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <InvestingBar value={signals?.marketValuation || 0} />
              </Box>
            </div>
          )}
        </Box>
        {showMask && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              px: 2,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              borderRadius: 3,
              background: theme.isDarkMode ? "rgba(35, 38, 41, 0.60)" : "rgba(242, 242, 242, 0.60)",
              backdropFilter: "blur(6px)",
            }}>
            <LockIcon />
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                maxWidth: "18.5rem",
                mt: 1,
                mb: 4.5,
                textAlign: "center",
              }}>
              {t("Metrics.signal.paywall")}
            </Typography>
            {/* <Link href="/membership">
              <Button variant="contained" color="primary" sx={{ fontWeight: 600, px: 4.5, py: 1.5 }}>
                {t("Metrics.signal.paywallBtn")}
              </Button>
            </Link> */}
          </Box>
        )}
      </Box>

      {/* 趋势指标说明 Dialog */}
      <Dialog
        open={trendHelpOpen}
        onClose={handleTrendHelpClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: "46.5rem",
              maxHeight: "60vh",
              borderRadius: "0.75rem",
              backgroundColor: "Dialog.bg",
              backdropFilter: "blur(6px)",
              color: "text.primary2",
            },
          },
        }}>
        <DialogTitle sx={{ pb: 5, px: 3, pt: 1.5, position: "relative" }}>
          {t("Metrics.signal.trendIndicator.title")}
          <IconButton
            onClick={handleTrendHelpClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ fontSize: "1.25rem", fontWeight: 600, mb: 2 }}>
            {t("Metrics.signal.trendIndicator.title")}
          </Typography>
          {t.rich("Metrics.signal.trendIndicator.introduction", pMap)}

          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mt: 6, mb: 2 }}>
            {t("Metrics.intro.howToUse")}
          </Typography>
          {t.rich("Metrics.signal.trendIndicator.howToUse", pMap)}
        </DialogContent>
      </Dialog>

      {/* 市场估值说明 Dialog */}
      <Dialog
        open={valuationHelpOpen}
        onClose={handleValuationHelpClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: "46.5rem",
              maxHeight: "60vh",
              borderRadius: "0.75rem",
              backgroundColor: "Dialog.bg",
              backdropFilter: "blur(6px)",
              color: "text.primary2",
            },
          },
        }}>
        <DialogTitle sx={{ pb: 5, px: 3, pt: 1.5, position: "relative" }}>
          {t("Metrics.signal.marketValuation.title")}
          <IconButton
            onClick={handleValuationHelpClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ fontSize: "1.25rem", fontWeight: 600, mb: 2 }}>
            {t("Metrics.signal.marketValuation.title")}
          </Typography>
          {t.rich("Metrics.signal.marketValuation.introduction", pMap)}

          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mt: 6, mb: 3.5 }}>
            {t("Metrics.signal.marketValuation.marketCycle")}
          </Typography>
          <Box sx={{ width: "36.5rem", margin: "0 auto" }}>
            <img src="/images/market-cycle.png" alt="market cycle" />
          </Box>

          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mt: 6, mb: 2 }}>
            {t("Metrics.intro.howToUse")}
          </Typography>
          {t.rich("Metrics.signal.marketValuation.howToUse", pMap)}

          <Typography variant="h5" sx={{ fontSize: "1.25rem", fontWeight: 600, mt: 6, mb: 2 }}>
            {t("Metrics.signal.important.title")}
          </Typography>
          {t.rich("Metrics.signal.important.desc", pMap)}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

const pMap = {
  p: (chunks: any) => (
    <Typography variant="body1" sx={{ mb: 2 }}>
      {chunks}
    </Typography>
  ),
  strong: (chunks: any) => (
    <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
      {chunks}
    </Typography>
  ),
};
